import { useReadContracts } from 'wagmi'
import { Address } from 'viem'
import { eEarnVaultImplementationAbi } from 'abis/evm/eEarnVaultImplementation'
import { eVaultImplementationAbi } from 'abis/evm/eVaultImplementation'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { calculateWeightedAPY, StrategyAPYData } from './calculateWeightedAPY'
import { E_EARN_VAULT_CHAIN_ID } from '@/config/euler-contracts'
import { useEarnVaultIndexer } from './useEarnVaultIndexer'

export interface UseEVaultWeightedAPYParams {
  vaultAddress: CheckedAddress
}

export interface UseEVaultWeightedAPYResult {
  weightedAPY: number
  isLoading: boolean
}

export function useEVaultWeightedAPY({ vaultAddress }: UseEVaultWeightedAPYParams): UseEVaultWeightedAPYResult {
  // Step 1: Try to get strategy data from indexer first (primary source)
  const {
    data: indexerData,
    isPending: isIndexerPending,
    isError: isIndexerError,
  } = useEarnVaultIndexer({
    chainId: E_EARN_VAULT_CHAIN_ID,
    address: vaultAddress as Address,
  })

  const indexerStrategies = indexerData?.vault?.strategies ?? []
  // Use indexer for strategy addresses only, but always use on-chain for shares (to match Euler's calculation)
  const hasIndexerStrategies =
    !isIndexerError && indexerData?.vault !== null && indexerData?.vault !== undefined && indexerStrategies.length > 0

  // Step 2: Get queue lengths (always fetch for on-chain shares)
  const { data: queueData, isLoading: isLoadingQueueLength } = useReadContracts({
    contracts: [
      {
        address: vaultAddress as Address,
        abi: eEarnVaultImplementationAbi,
        functionName: 'supplyQueueLength',
        chainId: E_EARN_VAULT_CHAIN_ID,
      },
      {
        address: vaultAddress as Address,
        abi: eEarnVaultImplementationAbi,
        functionName: 'withdrawQueueLength',
        chainId: E_EARN_VAULT_CHAIN_ID,
      },
    ],
    query: {
      staleTime: 30000,
      enabled: !hasIndexerStrategies, // Only fetch if we don't have indexer strategy addresses
    },
  })

  const supplyQueueLength = queueData?.[0]?.result as bigint | undefined
  const withdrawQueueLength = queueData?.[1]?.result as bigint | undefined

  // Step 3: Get all strategy addresses from BOTH queues (for onchain fallback)
  const supplyQueueContracts =
    supplyQueueLength && !hasIndexerStrategies
      ? Array.from({ length: Number(supplyQueueLength) }, (_, i) => ({
          address: vaultAddress as Address,
          abi: eEarnVaultImplementationAbi,
          functionName: 'supplyQueue' as const,
          args: [BigInt(i)],
          chainId: E_EARN_VAULT_CHAIN_ID,
        }))
      : []

  const withdrawQueueContracts =
    withdrawQueueLength && !hasIndexerStrategies
      ? Array.from({ length: Number(withdrawQueueLength) }, (_, i) => ({
          address: vaultAddress as Address,
          abi: eEarnVaultImplementationAbi,
          functionName: 'withdrawQueue' as const,
          args: [BigInt(i)],
          chainId: E_EARN_VAULT_CHAIN_ID,
        }))
      : []

  const { data: strategyAddressesData, isLoading: isLoadingStrategyAddresses } = useReadContracts({
    contracts: [...supplyQueueContracts, ...withdrawQueueContracts] as any,
    query: {
      enabled: !hasIndexerStrategies && (Number(supplyQueueLength || 0) > 0 || Number(withdrawQueueLength || 0) > 0),
      staleTime: 30000,
    },
  })

  // Get strategy addresses - use indexer if available, otherwise use onchain
  const strategyAddresses: Address[] = []

  if (hasIndexerStrategies) {
    // Use indexer for strategy addresses
    for (const strategy of indexerStrategies) {
      strategyAddresses.push(strategy.strategy as Address)
    }
  } else if (strategyAddressesData && supplyQueueLength !== undefined && withdrawQueueLength !== undefined) {
    // Use onchain data for strategy addresses
    const supplyCount = Number(supplyQueueLength)
    const withdrawCount = Number(withdrawQueueLength)

    for (let i = 0; i < supplyCount + withdrawCount; i++) {
      const addr = strategyAddressesData[i]?.result as Address | undefined
      if (addr) {
        strategyAddresses.push(addr)
      }
    }
  }

  // Step 4: Always get config for each strategy to get shares balance from on-chain (to match Euler's calculation)
  const { data: configData, isLoading: isLoadingConfig } = useReadContracts({
    contracts:
      strategyAddresses.length > 0
        ? (strategyAddresses.map((strategyAddress) => ({
            address: vaultAddress as Address,
            abi: eEarnVaultImplementationAbi,
            functionName: 'config',
            args: [strategyAddress],
            chainId: E_EARN_VAULT_CHAIN_ID,
          })) as any)
        : [],
    query: {
      enabled: strategyAddresses.length > 0,
      staleTime: 30000,
    },
  })

  // Get shares balances from on-chain config results
  const sharesBalances: bigint[] = []
  if (configData) {
    for (const result of configData) {
      const config = result?.result as any
      if (config) {
        const [balance] = config
        sharesBalances.push(balance as bigint)
      } else {
        sharesBalances.push(0n)
      }
    }
  }

  // Step 5: Get APY-related data from onchain (interestRate, totalBorrows, totalAssets, interestFee)
  // This is needed regardless of whether we use indexer or onchain for strategy addresses
  const strategyDataContracts = strategyAddresses.flatMap((strategyAddress) => [
    {
      address: strategyAddress,
      abi: eVaultImplementationAbi,
      functionName: 'interestRate',
      chainId: E_EARN_VAULT_CHAIN_ID,
    },
    {
      address: strategyAddress,
      abi: eVaultImplementationAbi,
      functionName: 'totalBorrows',
      chainId: E_EARN_VAULT_CHAIN_ID,
    },
    {
      address: strategyAddress,
      abi: eVaultImplementationAbi,
      functionName: 'totalAssets',
      chainId: E_EARN_VAULT_CHAIN_ID,
    },
    {
      address: strategyAddress,
      abi: eVaultImplementationAbi,
      functionName: 'interestFee',
      chainId: E_EARN_VAULT_CHAIN_ID,
    },
  ])

  const hasStrategies = strategyAddresses.length > 0 && sharesBalances.length > 0

  const { data: strategyData, isLoading } = useReadContracts({
    contracts: strategyDataContracts as any,
    query: {
      enabled: hasStrategies,
      staleTime: 30000,
    },
  })

  // Calculate weighted APY using shared utility
  const strategies: StrategyAPYData[] = []

  if (strategyData && strategyAddresses.length > 0) {
    for (let i = 0; i < strategyAddresses.length; i++) {
      const baseIndex = i * 4 // 4 fields per strategy (interestRate, totalBorrows, totalAssets, interestFee)
      const interestRate = strategyData[baseIndex]?.result as bigint | undefined
      const totalBorrows = strategyData[baseIndex + 1]?.result as bigint | undefined
      const totalAssets = strategyData[baseIndex + 2]?.result as bigint | undefined
      const interestFee = strategyData[baseIndex + 3]?.result as bigint | undefined

      // Get shares from on-chain config
      const shares = sharesBalances[i] || 0n

      if (shares > 0n && interestRate !== undefined && totalBorrows !== undefined && totalAssets !== undefined) {
        strategies.push({
          shares,
          interestRate,
          totalBorrows,
          totalAssets,
          interestFee,
        })
      }
    }
  }

  const weightedAPY = calculateWeightedAPY(strategies)

  // Determine loading state
  if (hasIndexerStrategies) {
    // Using indexer for strategy addresses, but on-chain for shares
    return {
      weightedAPY,
      isLoading: isIndexerPending || isLoadingConfig || isLoading || !strategyData,
    }
  }

  // Using full onchain fallback
  const totalQueueLength = Number(supplyQueueLength || 0) + Number(withdrawQueueLength || 0)
  const hasNoStrategies = supplyQueueLength !== undefined && withdrawQueueLength !== undefined && totalQueueLength === 0

  if (supplyQueueLength === undefined || withdrawQueueLength === undefined) {
    return {
      weightedAPY,
      isLoading: isLoadingQueueLength,
    }
  }

  if (hasNoStrategies) {
    return {
      weightedAPY,
      isLoading: false,
    }
  }

  return {
    weightedAPY,
    // Consider all loading states for onchain fallback
    isLoading: isLoadingStrategyAddresses || isLoadingConfig || (hasStrategies && isLoading),
  }
}
